import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Wrench, Package, MapPin, Phone, Mail, Heart, Share2, Eye, AlertCircle, X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../admin/lib/supabaseClient';
import type { ProjectRebuild } from '../admin/types';

import toast from 'react-hot-toast';


const PROJECT_TYPES = {
  'shell': { label: 'Shell', icon: '🏗️', description: 'Bare bodyshell' },
  'rolling-shell': { label: 'Rolling Shell', icon: '🛞', description: 'Shell with suspension & wheels' },
  'part-built': { label: 'Part Built', icon: '⚙️', description: 'Partially assembled project' },
  'project-car': { label: 'Project Car', icon: '🚗', description: 'Complete but needs work' },
  'chassis': { label: 'Chassis', icon: '🔩', description: 'Frame/subframes only' },
  'other': { label: 'Other', icon: '📦', description: 'Other rebuild projects' }
};

export default function ProjectRebuilds() {
  const [projects, setProjects] = useState<ProjectRebuild[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<ProjectRebuild | null>(null);
  const [activeModal, setActiveModal] = useState<'inquiry' | 'details' | null>(null);

  const [filters, setFilters] = useState({
    type: '',
    maxPrice: ''
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('project_rebuilds')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error loading project rebuilds:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => `R ${(price / 100).toLocaleString()}`;

  const filteredProjects = projects.filter(project => {
    if (filters.type && project.type !== filters.type) return false;
    if (filters.maxPrice && project.price > parseInt(filters.maxPrice) * 100) return false;
    return true;
  });

  const handleInquiry = (project: ProjectRebuild) => {
    setSelectedProject(project);
    setActiveModal('inquiry');
  };

  const handleViewDetails = (project: ProjectRebuild) => {
    setSelectedProject(project);
    setActiveModal('details');
  };



  return (
    <section id="project-rebuilds" className="py-16 bg-gradient-to-br from-mini-cream via-white to-mini-silver">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Project Rebuilds</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Mini shells, rolling shells, and project cars ready for your restoration dreams
          </p>
        </motion.div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
              >
                <option value="">All Types</option>
                {Object.entries(PROJECT_TYPES).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Price</label>
              <input
                type="number"
                placeholder="e.g., 50000"
                value={filters.maxPrice}
                onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ type: '', maxPrice: '' })}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm p-4 animate-pulse">
                <div className="aspect-video bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            <Package size={64} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Projects Available</h3>
            <p className="text-gray-600">Check back soon for new project rebuilds!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onInquire={handleInquiry}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        )}
      </div>

      {/* Inquiry Modal */}
      {activeModal === 'inquiry' && selectedProject && (
        <InquiryModal
          project={selectedProject}
          onClose={() => {
            setActiveModal(null);
            setSelectedProject(null);
          }}
        />
      )}

      {/* Details Modal */}
      {activeModal === 'details' && selectedProject && (
        <ProjectDetailsModal
          project={selectedProject}
          onClose={() => {
            setActiveModal(null);
            setSelectedProject(null);
          }}
          onInquire={() => {
            setActiveModal('inquiry');
          }}
        />
      )}


    </section>
  );
}

function ProjectCard({ project, onInquire, onViewDetails }: {
  project: ProjectRebuild;
  onInquire: (p: ProjectRebuild) => void;
  onViewDetails: (p: ProjectRebuild) => void;
}) {
  const formatPrice = (price: number) => `R ${(price / 100).toLocaleString()}`;
  const projectType = PROJECT_TYPES[project.type] || PROJECT_TYPES['other'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      onClick={() => onViewDetails(project)}
      className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow overflow-hidden cursor-pointer group"
    >
      {/* Image */}
      <div className="relative aspect-video overflow-hidden bg-gray-100">
        {project.image_url ? (
          <img
            src={project.image_url}
            alt={project.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <Package size={48} />
          </div>
        )}
        <div className="absolute top-3 left-3 bg-white/95 px-3 py-1.5 rounded-full z-10">
          <span className="text-sm font-semibold">{projectType.icon} {projectType.label}</span>
        </div>

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <div className="bg-white/90 text-gray-900 px-4 py-2 rounded-full font-medium opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0">
            View Details
          </div>
        </div>
        {project.status !== 'available' && (
          <div className={`absolute top-3 right-3 px-3 py-1.5 rounded-full text-xs font-semibold ${project.status === 'sold' ? 'bg-red-500 text-white' : 'bg-yellow-500 text-white'
            }`}>
            {project.status.replace('-', ' ').toUpperCase()}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>

        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
          {project.year && (
            <div className="flex items-center gap-1">
              <Calendar size={16} />
              {project.year}
            </div>
          )}
          {project.color && (
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: project.color }} />
              {project.color}
            </div>
          )}
        </div>

        {project.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{project.description}</p>
        )}

        {/* What's Included */}
        {project.includes && project.includes.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-700 mb-1">Includes:</p>
            <div className="flex flex-wrap gap-1">
              {project.includes.slice(0, 3).map((item, i) => (
                <span key={i} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                  {item}
                </span>
              ))}
              {project.includes.length > 3 && (
                <span className="text-xs text-gray-500">+{project.includes.length - 3} more</span>
              )}
            </div>
          </div>
        )}

        {/* Price & Action */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-2xl font-bold text-mini-red">
            {formatPrice(project.price)}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onInquire(project);
            }}
            disabled={project.status !== 'available'}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${project.status === 'available'
              ? 'bg-mini-red text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            {project.status === 'available' ? 'Inquire' : 'Unavailable'}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function InquiryModal({ project, onClose }: { project: ProjectRebuild; onClose: () => void }) {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    message: '',
    inquiry_type: 'general' as any
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isSupabaseConfigured) {
      toast.error('Database not configured');
      return;
    }

    try {
      setSubmitting(true);
      const { error } = await supabase.from('project_rebuild_inquiries').insert([{
        project_rebuild_id: project.id,
        ...formData
      }]);

      if (error) throw error;

      toast.success('Inquiry sent successfully! We\'ll be in touch soon.');
      onClose();
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        <h3 className="text-2xl font-bold mb-4">Inquire About {project.title}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
            <input
              type="text"
              required
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              required
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Inquiry Type</label>
            <select
              value={formData.inquiry_type}
              onChange={(e) => setFormData({ ...formData, inquiry_type: e.target.value as any })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
            >
              <option value="general">General Inquiry</option>
              <option value="viewing">Schedule Viewing</option>
              <option value="offer">Make an Offer</option>
              <option value="purchase">Ready to Purchase</option>
              <option value="shipping">Shipping Information</option>
              <option value="details">Request More Details</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about your interest in this project..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-mini-red text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Sending...' : 'Send Inquiry'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function ProjectDetailsModal({ project, onClose, onInquire }: {
  project: ProjectRebuild;
  onClose: () => void;
  onInquire: () => void;
}) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = project.images && project.images.length > 0
    ? project.images
    : (project.image_url ? [project.image_url] : []);

  const formatPrice = (price: number) => `R ${(price / 100).toLocaleString()}`;
  const projectType = PROJECT_TYPES[project.type] || PROJECT_TYPES['other'];

  const nextImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              {project.title}
              <span className="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {projectType.label}
              </span>
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-8">
            {/* Image Gallery */}
            <div className="bg-gray-900 relative aspect-video lg:aspect-auto min-h-[300px] flex items-center justify-center lg:self-start">
              {images.length > 0 ? (
                <>
                  <img
                    src={images[currentImageIndex]}
                    alt={`${project.title} - View ${currentImageIndex + 1}`}
                    className="w-full h-auto max-h-[70vh] object-contain"
                  />

                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                      >
                        <ChevronLeft size={24} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                      >
                        <ChevronRight size={24} />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {images.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentImageIndex(idx);
                            }}
                            className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                              }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="text-gray-500 flex flex-col items-center">
                  <Package size={64} className="mb-4" />
                  <p>No images available</p>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6 lg:p-8 space-y-6">
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-mini-red">
                  {formatPrice(project.price)}
                </div>
                <div className={`px-3 py-1.5 rounded-full text-sm font-semibold ${project.status === 'available'
                  ? 'bg-green-100 text-green-700'
                  : project.status === 'sold'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-yellow-100 text-yellow-700'
                  }`}>
                  {project.status === 'available' ? 'Available Now' : project.status.replace('-', ' ').toUpperCase()}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                {project.year && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar size={18} className="text-gray-400" />
                    <span>Year: <strong>{project.year}</strong></span>
                  </div>
                )}
                {project.color && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <div className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: project.color }} />
                    <span>Color: <strong>{project.color}</strong></span>
                  </div>
                )}
                {project.location && (
                  <div className="flex items-center gap-2 text-gray-700 col-span-2">
                    <MapPin size={18} className="text-gray-400" />
                    <span>Location: <strong>{project.location}</strong></span>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                <p className="text-gray-600 whitespace-pre-wrap">{project.description}</p>
              </div>

              {/* Lists */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {project.includes && project.includes.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Check size={16} className="text-green-500" />
                      Includes
                    </h4>
                    <ul className="space-y-1">
                      {project.includes.map((item, i) => (
                        <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {project.missing_parts && project.missing_parts.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <AlertCircle size={16} className="text-amber-500" />
                      Missing Parts
                    </h4>
                    <ul className="space-y-1">
                      {project.missing_parts.map((item, i) => (
                        <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {project.rust_condition && (
                <div className="bg-orange-50 border border-orange-100 p-4 rounded-lg">
                  <h4 className="font-semibold text-orange-900 mb-1 flex items-center gap-2">
                    <Wrench size={16} />
                    Rust Condition
                  </h4>
                  <p className="text-sm text-orange-800">{project.rust_condition}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t flex gap-3 justify-end bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={onInquire}
            disabled={project.status !== 'available'}
            className={`px-8 py-2 rounded-lg font-medium transition-colors ${project.status === 'available'
              ? 'bg-mini-red text-white hover:bg-red-700 shadow-md hover:shadow-lg'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            {project.status === 'available' ? 'Inquire Interest' : 'Unavailable'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
